import { useState, useEffect } from "react";
import { X, Delete } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Category, SCHOOL_CATEGORIES } from "../../types/game";

interface Props {
  category: Category;
  current: number | null;
  currentFromHand?: boolean;
  suggestedValue?: number;
  playerName: string;
  onConfirm: (value: number | null, fromHand?: boolean) => void;
  onClose: () => void;
}

function SchoolInput({
  category,
  current,
  suggestedValue,
  onConfirm,
  onClose,
}: Omit<Props, "playerName">) {
  const { t } = useTranslation();
  const face = SCHOOL_CATEGORIES.indexOf(category as any) + 1;
  const [selected, setSelected] = useState<number | null>(
    current !== null ? current / face + 3 : null,
  );

  const getBalance = (count: number) => {
    const sum = count * face;
    return sum - 3 * face;
  };
  const labelFor = (count: number) => {
    const b = getBalance(count);
    return b === 0 ? "✕" : b > 0 ? `+${b}` : `${b}`;
  };
  const colorFor = (count: number) => {
    const b = getBalance(count);
    return b > 0 ? "text-green-400" : b < 0 ? "text-red-400" : "text-yellow-300";
  };

  const suggestedCount = suggestedValue !== undefined ? (suggestedValue / face) + 3 : null;

  return (
    <div>
      <p className="text-sm text-ink/40 text-center mb-4">
        {t("scoreInput.howMany", { n: face })}
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6">
        {[0, 1, 2, 3, 4, 5].map((count) => (
          <button
            key={count}
            onClick={() => setSelected(count)}
            className={`relative flex flex-col items-center justify-center py-3 px-1 rounded-xl border-2 transition-colors gap-1 ${
              selected === count
                ? "border-blue-500 bg-blue-600/20"
                : "border-ink/15 glass hover:border-ink/30"
            }`}
          >
            {count === suggestedCount && (
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[8px] px-1 rounded-full uppercase font-black tracking-tighter">
                {t("scoreInput.tip")}
              </span>
            )}
            <div className="flex flex-wrap justify-center gap-px min-h-[1.25rem] items-center leading-none">
              {count === 0
                ? <span className="text-ink/30 text-lg leading-none">—</span>
                : Array.from({ length: count }).map((_, i) => (
                    <span key={i} className="text-base leading-none">🎲</span>
                  ))
              }
            </div>
            <span className={`text-sm font-black ${colorFor(count)}`}>
              {labelFor(count)}
            </span>
          </button>
        ))}
      </div>
      
      <div className="flex gap-3">
        {current !== null && (
          <button
            onClick={() => {
              onConfirm(null);
              onClose();
            }}
            className="flex-1 py-3 glass-btn rounded-xl text-sm text-ink/70 transition-colors"
          >
            {t("scoreInput.clear")}
          </button>
        )}
        <button
          onClick={() => {
            if (selected !== null) {
              onConfirm(getBalance(selected));
              onClose();
            }
          }}
          disabled={selected === null}
          className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-xl font-semibold text-white transition-colors"
        >
          {selected !== null
            ? `${t("scoreInput.confirm")} (${labelFor(selected)})`
            : t("scoreInput.selectCount")}
        </button>
      </div>
      <div className="h-safe" />
    </div>
  );
}

function ComboInput({
  category,
  current,
  currentFromHand = false,
  suggestedValue,
  onConfirm,
  onClose,
}: Omit<Props, "playerName">) {
  const { t } = useTranslation();
  const isFixed = category === "straight1" || category === "straight2";
  const fixedVal = category === "straight1" ? 15 : 20;

  // Flat bonus categories — ×2 from hand applies to dice sum only; bonus is always added flat
  const BONUS_AMOUNTS: Record<string, number> = {
    full_house: 10,
    four_of_kind: 20,
    poker: 50,
  };
  const bonusAmount = BONUS_AMOUNTS[category] ?? 0;
  const isBonusCat = bonusAmount > 0;

  const [raw, setRaw] = useState(() => {
    if (current === null || current === 0) return "";
    // Reverse-engineer the raw dice sum from the stored value:
    // Stored = (raw × 2 if fromHand) + bonusAmount; fromHand is persisted on
    // the scorecard, so the prefill is exact.
    const remaining = isBonusCat ? current - bonusAmount : current;
    const rawVal = currentFromHand ? Math.floor(remaining / 2) : remaining;
    return String(Math.max(0, rawVal));
  });
  const [isFromHand, setIsFromHand] = useState(currentFromHand);

  const append = (d: string) => setRaw((p) => (p.length < 3 ? p + d : p));
  const del = () => setRaw((p) => p.slice(0, -1));
  const val = raw === "" ? null : parseInt(raw, 10);
  const diceSum = val !== null ? (isFromHand ? val * 2 : val) : null;
  const finalVal = diceSum !== null
    ? (isBonusCat ? diceSum + bonusAmount : diceSum)
    : null;
  const fixedFinalVal = isFromHand ? fixedVal * 2 : fixedVal;

  if (isFixed)
    return (
      <div className="space-y-3">
        {/* From Hand Checkbox */}
        <div className="flex items-center justify-center gap-3 mb-4 glass p-3 rounded-xl border border-ink/10 hover:border-ink/20 transition-colors cursor-pointer" onClick={() => setIsFromHand(!isFromHand)}>
          <div className={`w-6 h-6 rounded flex items-center justify-center border-2 transition-colors ${isFromHand ? "bg-blue-500 border-blue-500" : "border-gray-500"}`}>
            {isFromHand && <span className="text-white text-sm font-bold">✓</span>}
          </div>
          <span className="text-ink/80 font-medium select-none">{t("scoreInput.fromHand")}</span>
        </div>

        <button
          onClick={() => {
            onConfirm(fixedFinalVal, isFromHand);
            onClose();
          }}
          className="w-full py-4 bg-green-600 hover:bg-green-500 rounded-xl font-bold text-lg text-white transition-colors"
        >
          {t("scoreInput.scoreFixed", { pts: fixedFinalVal })}
        </button>
        <button
          onClick={() => {
            onConfirm(0);
            onClose();
          }}
          className="w-full py-3 bg-red-900/50 text-red-400 hover:bg-red-800/60 border border-red-800 rounded-xl font-semibold transition-colors"
        >
          {t("scoreInput.crossOut")}
        </button>
        {current !== null && (
          <button
            onClick={() => {
              onConfirm(null);
              onClose();
            }}
            className="w-full py-2.5 text-sm text-ink/40 hover:text-ink/70 transition-colors"
          >
            {t("scoreInput.clear")}
          </button>
        )}
        <div className="h-safe" />
      </div>
    );

  return (
    <div>
      {suggestedValue !== undefined && suggestedValue > 0 && (
        <button
          onClick={() => {
            onConfirm(suggestedValue);
            onClose();
          }}
          className="w-full mb-4 py-3 bg-blue-600/30 border border-blue-500/50 rounded-xl flex items-center justify-between px-4 hover:bg-blue-600/40 transition-colors group"
        >
          <div className="flex flex-col items-start text-left">
            <span className="text-[10px] text-blue-400 font-bold uppercase tracking-wider">{t("scoreInput.suggested")}</span>
            <span className="text-lg font-black text-ink">{suggestedValue} pts</span>
          </div>
          <div className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-bold group-active:scale-95 transition-transform">{t("scoreInput.record")}</div>
        </button>
      )}

      <div className="glass rounded-xl h-16 flex items-center justify-center mb-4 gap-2">
        <span
          className={`text-4xl font-mono font-bold ${raw ? "text-ink" : "text-ink/30"}`}
        >
          {raw || "0"}
        </span>
        {isBonusCat && val !== null && (
          <span className="text-lg font-semibold text-green-400">
            {isFromHand ? `×2 +${bonusAmount} = ${finalVal}` : `+${bonusAmount} = ${finalVal}`}
          </span>
        )}
      </div>
      {/* Numpad */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"].map(
          (k, i) =>
            k === "" ? (
              <div key={i} />
            ) : k === "⌫" ? (
              <button
                key={i}
                onClick={del}
                className="py-4 glass-btn rounded-xl flex items-center justify-center transition-colors"
              >
                <Delete size={18} className="text-ink/70" />
              </button>
            ) : (
              <button
                key={i}
                onClick={() => append(k)}
                className="py-4 glass-btn rounded-xl text-xl font-semibold transition-colors"
              >
                {k}
              </button>
            ),
        )}
      </div>

      {/* From Hand Checkbox */}
      <div className="flex items-center justify-center gap-3 mb-2 glass p-3 rounded-xl border-ink/10 hover:border-ink/20 transition-colors cursor-pointer" onClick={() => setIsFromHand(!isFromHand)}>
        <div className={`w-6 h-6 rounded flex items-center justify-center border-2 transition-colors ${isFromHand ? "bg-blue-500 border-blue-500" : "border-gray-500"}`}>
          {isFromHand && <span className="text-white text-sm font-bold">✓</span>}
        </div>
        <span className="text-ink/80 font-medium select-none">{t("scoreInput.fromHand")}</span>
      </div>

      {/* Bonus indicator for flat-bonus categories */}
      {isBonusCat && (
        <div className="flex items-center justify-center gap-2 mb-4 bg-green-900/30 p-3 rounded-xl border border-green-800/50">
          <span className="text-green-400 font-bold text-lg">+{bonusAmount}</span>
          <span className="text-green-300/80 text-sm font-medium select-none">{t("scoreInput.flatBonus")}</span>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => {
            onConfirm(0);
            onClose();
          }}
          className="flex-1 py-3 bg-red-900/50 text-red-400 hover:bg-red-800/60 border border-red-800 rounded-xl font-semibold text-sm transition-colors"
        >
          {t("scoreInput.crossOut")}
        </button>
        {current !== null && (
          <button
            onClick={() => {
              onConfirm(null);
              onClose();
            }}
            className="px-4 py-3 glass-btn rounded-xl text-sm text-ink/50 transition-colors"
          >
            {t("scoreInput.clear")}
          </button>
        )}
        <button
          onClick={() => {
            onConfirm(finalVal ?? 0, isFromHand && finalVal !== null);
            onClose();
          }}
          className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl font-semibold text-white transition-colors"
        >
          {t("scoreInput.confirm")} {finalVal !== null ? `(${finalVal})` : ""}
        </button>
      </div>
      <div className="h-safe" />
    </div>
  );
}

export default function ScoreInputSheet({
  category,
  current,
  currentFromHand = false,
  suggestedValue,
  playerName,
  onConfirm,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const isSchool = SCHOOL_CATEGORIES.includes(category as any);

  // Lock background scroll while sheet is open. The sheet uses position:absolute
  // inside a position:relative full-height container — avoiding position:fixed
  // which is unreliable in iOS WKWebView / Telegram Mini App on iPhone.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="absolute inset-0 z-50 flex items-end justify-center bg-black/70"
      onClick={onClose}
      style={{ touchAction: "none" }}
    >
      <div
        className="w-full max-w-md glass-modal rounded-t-3xl p-5 max-h-[90dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ touchAction: "pan-y" }}
      >
        <div className="w-10 h-1 bg-ink/20 rounded-full mx-auto mb-4" />
        <div className="flex items-center justify-between mb-1">
          <div>
            <h3 className="font-bold text-lg">{t(`categories.${category}`)}</h3>
            <p className="text-xs text-ink/35">
              {playerName} · {t(`hints.${category}`)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-ink/35 hover:text-ink"
          >
            <X size={20} />
          </button>
        </div>
        <div className="my-4 h-px bg-ink/10" />
        {isSchool ? (
          <SchoolInput
            category={category}
            current={current}
            suggestedValue={suggestedValue}
            onConfirm={onConfirm}
            onClose={onClose}
          />
        ) : (
          <ComboInput
            category={category}
            current={current}
            currentFromHand={currentFromHand}
            suggestedValue={suggestedValue}
            onConfirm={onConfirm}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}
